import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

import { ValidationType } from '../configs'
import { initFormField, resetModifiedDataByField, updateFormDataField } from '../formSlice'
import {
  createFormDataFieldSelector,
  isValidationTypeOnBlur,
  isValidationTypeOnChange,
  safeShallowEqual,
} from '../helpers'
import '../helpers/checkValidationType'
import { IFormField } from '../models'
import { FormValidationBuilder } from '../utils/validation'

import { useHandleValidate } from './useHandleValidate'

/*
 * Hook to generate a value and a handler method for working with form store.
 * */
export const useFormField = <T,>({
  defaultValue,
  field,
  formKey,
  onChange,
  onBlur,
  onValidate,
  onEmptyCheck,
  isDisabled,
  isRequired,
  isReadOnly,
  isVisible = true,
  validationType = ValidationType.ONBLUR,
}: IFormField<T>) => {
  const dispatch = useDispatch()
  const selectField = useMemo(
    () => createFormDataFieldSelector<T>(formKey, field),
    [field, formKey]
  )
  const formField = useSelector(
    selectField,
    (prev, next) =>
      safeShallowEqual(prev.value, next.value) &&
      prev.validationMessage === next.validationMessage &&
      prev.isModified === next.isModified
  )
  const { validationMessage, isModified } = formField
  const isNotInit = formField.value === undefined
  const value = useMemo(
    () => (isNotInit ? defaultValue : formField.value),
    [defaultValue, formField.value, isNotInit]
  )
  const isDefaultValueUpdated = useMemo(() => {
    return !isModified && !safeShallowEqual(value, defaultValue)
  }, [defaultValue, isModified, value])
  const { handleValidate } = useHandleValidate({
    onValidate,
    onEmptyCheck,
    field,
    formKey,
    isDisabled,
    isRequired,
    isReadOnly,
    isVisible,
  })
  const debounceTimer = 500
  const debouncedHandleValidate = useDebouncedCallback(handleValidate, debounceTimer, {
    leading: true,
  })
  const formValidation = FormValidationBuilder.build(formKey)
  formValidation.add<T>({ field, handler: handleValidate })

  const handleChange = useCallback(
    async (newValue: T, ...args: []) => {
      dispatch(updateFormDataField({ formKey, field, value: newValue }))

      if (isValidationTypeOnChange(validationType)) {
        await debouncedHandleValidate(newValue)
      }

      if (onChange) {
        onChange(newValue, ...args)
      }
    },
    [debouncedHandleValidate, dispatch, field, formKey, onChange, validationType]
  )

  const handleBlur = useCallback(async () => {
    if (isValidationTypeOnBlur(validationType)) {
      await handleValidate(value)
    }
    if (onBlur) {
      onBlur()
    }
  }, [handleValidate, onBlur, validationType, value])

  useEffect(() => {
    if (isNotInit || isDefaultValueUpdated) {
      dispatch(initFormField({ formKey, field, defaultValue }))
    }
  }, [defaultValue, dispatch, field, formKey, isDefaultValueUpdated, isNotInit])

  useEffect(() => {
    if (isModified && safeShallowEqual(defaultValue, value)) {
      dispatch(resetModifiedDataByField({ formKey, field }))
    }
  }, [isModified, defaultValue, value, field, formKey, dispatch])

  return { handleBlur, value, validationMessage, handleChange, isModified }
}
